/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { NavigationButton, NavigationButtonType } from "@octopusdeploy/design-system-components";
import { logger } from "@octopusdeploy/logging";
import type { PerformanceConfigurationResource, ProjectResource, TenantResource } from "@octopusdeploy/octopus-server-client";
import { DashboardRenderMode, Permission, Repository, TenantedDeploymentMode } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { DynamicDeploymentsOverview } from "app/areas/projects/components/DynamicDeploymentsOverview/DynamicDeploymentsOverview";
import { isEqual } from "lodash";
import * as React from "react";
import type { ActionEvent, AnalyticActionDispatcher } from "~/analytics/Analytics";
import { Action, ProjectAnalyticView, useProjectScopedAnalyticActionDispatch } from "~/analytics/Analytics";
import type { RenderDashboardProps } from "~/areas/dashboard/DashboardOverview/DashboardOverviewPage";
import type { DeploymentOverviewFilters } from "~/areas/projects/components/DashboardDataSource/DataCube";
import { DimensionTypes } from "~/areas/projects/components/DashboardDataSource/DataCube";
import TenantedProjectDashboardDataSource from "~/areas/projects/components/DashboardDataSource/TenantedProjectDashboardDataSource";
import UntenantedProjectDashboardDataSource from "~/areas/projects/components/DashboardDataSource/UntenantedProjectDashboardDataSource";
import OverviewFilters from "~/areas/projects/components/DeploymentsOverview/OverviewFilters";
import useDeploymentsOverviewFilters from "~/areas/projects/components/DeploymentsOverview/hooks/useDeploymentsOverviewFilters";
import type { SampleProjectTourContextProps } from "~/areas/projects/components/ProjectLayout/SampleProjectTour/SampleProjectTour";
import { SampleProjectTourContext } from "~/areas/projects/components/ProjectLayout/SampleProjectTour/SampleProjectTour";
import SampleProjectTourStartTourOnMount from "~/areas/projects/components/ProjectLayout/SampleProjectTour/SampleProjectTourStartTourOnMount";
import { ProjectPageLayout } from "~/areas/projects/components/ProjectPageLayout";
import { useProjectContext } from "~/areas/projects/context/ProjectContext";
import type { WithProjectContextInjectedProps } from "~/areas/projects/context/withProjectContext";
import type { PageState } from "~/areas/tenants/components/Paging/usePageState";
import { usePageState } from "~/areas/tenants/components/Paging/usePageState";
import { repository } from "~/clientInstance";
import ActionList from "~/components/ActionList";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import type { OverflowMenuGenericItem, OverflowMenuNavLink } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import type { PrimaryPageAction } from "~/components/PageActions/PageActions";
import PermissionCheck, { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import SampleProcessButtonWithRedirect from "~/components/ProjectBasedActivation/SampleProcessButtonWithRedirect";
import { useIsPageHeaderVNextEnabled } from "~/components/RootRoutes/useIsPageHeaderVNextEnabled";
import { useRequiredContext } from "~/hooks/index";
import { Callout, CalloutType } from "~/primitiveComponents/dataDisplay/Callout/Callout";
import { RecentProjects } from "~/utils/RecentProjects/RecentProjects";
import type { DataBaseComponentState } from "../../../../components/DataBaseComponent/DataBaseComponent";
import { DataBaseComponent } from "../../../../components/DataBaseComponent/DataBaseComponent";
import ProjectDashboard from "../ProjectDashboard";
import { ProjectStatus } from "../ProjectStatus/ProjectStatus";
import Onboarding from "./Onboarding";
interface OverviewState extends DataBaseComponentState {
    project: ProjectResource;
    tenants: TenantResource[];
    hasSteps: boolean;
    hasReleases: boolean;
    failedChecks: Array<{
        permission: Permission;
        isNotAllowed: boolean;
    }>;
    dashboardRenderMode: DashboardRenderMode;
    isDashboardLoaded: boolean;
}
interface DeploymentsOverviewPropsInternal extends WithProjectContextInjectedProps {
    sampleProjectTourContext: SampleProjectTourContextProps;
    dispatchAction: AnalyticActionDispatcher;
    isPageHeaderVNextEnabled: boolean;
    filters: DeploymentOverviewFilters;
    setFilters: (newFilters: DeploymentOverviewFilters) => void;
    pageState: PageState;
    setPageState: (newPageNumber: number, newPageSize: number) => void;
}
class DeploymentsOverviewInternal extends DataBaseComponent<DeploymentsOverviewPropsInternal, OverviewState> {
    dynamicEnvironmentsEnabled = isFeatureToggleEnabled("DynamicEnvironmentsFeatureToggle");
    constructor(props: DeploymentsOverviewPropsInternal) {
        super(props);
        this.state = {
            hasSteps: false,
            hasReleases: false,
            project: null!,
            tenants: [],
            failedChecks: [],
            dashboardRenderMode: null!,
            isDashboardLoaded: false,
        };
    }
    async componentDidMount() {
        await this.doBusyTask(async () => {
            const tenants = isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? await repository.Tenants.all() : [];
            const { model: project, projectContextRepository } = this.props.projectContext.state;
            const performanceConfiguration = await repository.PerformanceConfiguration.get();
            await RecentProjects.getInstance().UpdateAccessedProjectIntoLocalStorage(project.Id);
            const requiredPermissions = [
                { permission: Permission.ProjectView, project: project.Id, tenant: "*", projectGroup: project.ProjectGroupId },
                { permission: Permission.ReleaseView, project: project.Id, tenant: "*", projectGroup: project.ProjectGroupId },
                { permission: Permission.EnvironmentView, wildcard: true },
            ];
            const failedChecks = requiredPermissions
                .map((check) => ({
                permission: check.permission,
                isNotAllowed: !isAllowed(check),
            }))
                .filter((check) => check.isNotAllowed);
            if (failedChecks.length > 0) {
                this.setState({
                    project,
                    failedChecks,
                });
                return;
            }
            let hasSteps = false;
            try {
                const deploymentProcess = await projectContextRepository.DeploymentProcesses.get();
                hasSteps = deploymentProcess.Steps.length > 0;
            }
            catch (exception) {
                logger.error(exception, "Failed to get deployment processes for {project}", { project });
            }
            let hasReleases = false;
            try {
                const releases = await repository.Projects.getReleases(project, { take: 1 });
                hasReleases = releases.TotalResults > 0;
            }
            catch (exception) {
                logger.error(exception, "Failed to get releases for {project}", { project });
            }
            // The untenanted project dashboard is grouped by channel by default
            if (project.TenantedDeploymentMode === TenantedDeploymentMode.Untenanted) {
                this.props.setFilters({ groupBy: DimensionTypes.Channel });
            }
            this.setState({
                project,
                tenants,
                hasSteps,
                hasReleases,
                dashboardRenderMode: this.getDashboardRenderMode(project, performanceConfiguration),
            });
        });
    }
    render() {
        if (!this.state.project) {
            return <ProjectPageLayout busy={this.state.busy} errors={this.errors}/>;
        }
        if (this.state.failedChecks.length > 0) {
            return (<ProjectPageLayout busy={this.state.busy} errors={this.errors} title="Deployments" breadcrumbTitle={this.state.project.Name}>
                    <Callout type={CalloutType.Information} title={"Permission required"}>
                        The {this.state.failedChecks[0].permission} permission is required to view project overview details
                    </Callout>
                </ProjectPageLayout>);
        }
        if (this.state.project && (this.state.project.IsVersionControlled ? !this.state.hasReleases : !this.state.hasSteps)) {
            return (<ProjectPageLayout busy={this.state.busy} errors={this.errors} title="Deployments" breadcrumbTitle={this.state.project.Name} statusSection={<ProjectStatus doBusyTask={this.doBusyTask}/>}>
                    <Onboarding project={this.state.project} actionButtons={<PermissionCheck permission={Permission.ProcessEdit} project={this.state.project.Id} tenant="*">
                                <ActionList alignStart={true} actions={[
                        <NavigationButton label="Create Process" href={links.deploymentProcessStepsPage.generateUrl({ spaceId: this.state.project.SpaceId, projectSlug: this.state.project.Slug }, { stepTemplates: true })} type={NavigationButtonType.Primary} onClick={() => {
                                const ev: ActionEvent = {
                                    action: Action.Add,
                                    resource: "Deployment Process",
                                };
                                this.props.dispatchAction("Define your Deployment Process", ev);
                            }}/>,
                        <SampleProcessButtonWithRedirect project={this.props.projectContext.state.model}/>,
                    ]}/>
                            </PermissionCheck>}/>
                    <ProjectAnalyticView resource="Project" projectId={this.state.project.Id}/>
                </ProjectPageLayout>);
        }
        let primaryPageAction: PrimaryPageAction | undefined = undefined;
        const createReleaseIsAllowed = this.props.isPageHeaderVNextEnabled && (this.state.hasSteps || this.state.project?.IsVersionControlled);
        if (createReleaseIsAllowed) {
            const createReleaseButton: PrimaryPageAction = {
                type: "navigate",
                label: "Create Release",
                path: links.createReleasePage.generateUrl({ spaceId: this.state.project.SpaceId, projectSlug: this.state.project.Slug }),
                onClick: () => this.props.dispatchAction("Create a release", { resource: "Create Release", action: Action.Add }),
                hasPermissions: isAllowed({ permission: Permission.ReleaseCreate, project: this.state.project.Id, tenant: "*" }),
            };
            primaryPageAction = createReleaseButton;
        }
        return (<>
                <ProjectPageLayout title={this.props.isPageHeaderVNextEnabled ? "Deployments" : "Overview"} breadcrumbTitle={this.state.project.Name} busy={this.state.isDashboardLoaded ? false : this.state.busy} errors={this.errors} overflowActions={[this.getDashboardRenderModeOverflowMenuItem(), this.getAuditTrailMenuItem()]} disableHeaderAnimations={true} // Disabled because of the way the ProjectOverview renders multiple PaperLayouts.
         fullWidth={true} statusSection={<ProjectStatus doBusyTask={this.doBusyTask}/>} primaryAction={primaryPageAction}>
                    {this.state.project.TenantedDeploymentMode === TenantedDeploymentMode.Untenanted ? (<UntenantedProjectDashboardDataSource project={this.state.project} render={this.renderDashboard} doBusyTask={this.doBusyTask} onDashboardLoad={() => this.setState({ isDashboardLoaded: true })}/>) : (<TenantedProjectDashboardDataSource project={this.state.project} filters={this.props.filters} render={this.renderDashboard} doBusyTask={this.doBusyTask} onDashboardLoad={(filters, pageNumber, pageSize) => this.onDashboardLoad(filters, pageNumber, pageSize)} pageNumber={this.props.pageState.pageNumber} pageSize={this.props.filters.groupBy ? Repository.takeAll : this.props.pageState.pageSize}/>)}
                </ProjectPageLayout>
                {this.dynamicEnvironmentsEnabled && (<ProjectPageLayout fullWidth={true}>
                        <DynamicDeploymentsOverview spaceId={this.state.project.SpaceId} projectSlug={this.state.project.Slug} doBusyTask={this.doBusyTask}/>
                    </ProjectPageLayout>)}
            </>);
    }
    private onDashboardLoad(filters: DeploymentOverviewFilters, pageNumber: number, pageSize: number) {
        // Requests with outdated parameters should be ignored, otherwise the loading bar will be hidden even though the latest request is ongoing
        const isQueryParamsUpToDate = isEqual(filters, this.props.filters) && pageNumber === this.props.pageState.pageNumber && pageSize === this.props.pageState.pageSize;
        if (isQueryParamsUpToDate) {
            this.setState({ isDashboardLoaded: true });
        }
    }
    private showLoadingDashboard(dataSource: RenderDashboardProps) {
        return (<ProjectDashboard spaceId={this.props.projectContext.state.model.SpaceId} cube={dataSource.cube!} filters={this.props.filters} allowDeployments={true} flatStyle={true} dashboardRenderMode={this.state.dashboardRenderMode} project={this.state.project} pageNumber={this.props.pageState.pageNumber} pageSize={this.props.filters.groupBy ? Repository.takeAll : this.props.pageState.pageSize} totalItems={dataSource.totalItems} onPagingSelectionChange={(newPageNumber, newPageSize) => this.updatePage(newPageNumber, newPageSize)}/>);
    }
    private updatePage(newPageNumber: number, newPageSize: number) {
        this.setState({ isDashboardLoaded: false });
        this.props.setPageState(newPageNumber, newPageSize);
    }
    private localStorageKeyForVirtualDashboard(project: ProjectResource): string {
        return "virtualDashboard" + project.Id;
    }
    private getDashboardRenderMode(project: ProjectResource, performanceConfiguration: PerformanceConfigurationResource): DashboardRenderMode {
        const localRenderMode = localStorage.getItem(this.localStorageKeyForVirtualDashboard(project));
        if (localRenderMode !== null) {
            const typedRenderModeString = localRenderMode as keyof typeof DashboardRenderMode;
            return DashboardRenderMode[typedRenderModeString];
        }
        return performanceConfiguration.DefaultDashboardRenderMode;
    }
    private setDashboardRenderMode(value: DashboardRenderMode) {
        localStorage.setItem(this.localStorageKeyForVirtualDashboard(this.state.project), value.toString());
        this.setState({ dashboardRenderMode: value });
    }
    private filtersUpdated = (filters: DeploymentOverviewFilters) => {
        this.setState({ isDashboardLoaded: false });
        this.props.setPageState(1, this.props.pageState.pageSize);
        this.props.setFilters(filters);
    };
    private renderDashboard = (dataSource: RenderDashboardProps) => {
        if (!dataSource.cube) {
            return <></>;
        }
        return (<SampleProjectTourStartTourOnMount>
                <OverviewFilters cube={dataSource.cube} tenants={this.state.tenants} filters={this.props.filters} doBusyTask={this.doBusyTask} defaultFilter={this.createDefaultFilter(this.state.project)} onFiltersUpdated={this.filtersUpdated} project={this.state.project} render={() => this.showLoadingDashboard(dataSource)}/>
            </SampleProjectTourStartTourOnMount>);
    };
    private getDashboardRenderModeOverflowMenuItem = (): OverflowMenuGenericItem => {
        const text = this.state.dashboardRenderMode === DashboardRenderMode.VirtualizeColumns ? "Switch to fast rendering" : "Switch to full rendering";
        const oppositeSetting = this.state.dashboardRenderMode === DashboardRenderMode.VirtualizeColumns ? DashboardRenderMode.VirtualizeRowsAndColumns : DashboardRenderMode.VirtualizeColumns;
        return OverflowMenuItems.item(text, () => this.setDashboardRenderMode(oppositeSetting), undefined, "Full rendering will render all rows, fast rendering will render only visible rows");
    };
    private getAuditTrailMenuItem = (): OverflowMenuNavLink => {
        return OverflowMenuItems.navItem("Audit Trail", links.auditPage.generateUrl({ projects: [this.state.project.Id] }), {
            permission: Permission.EventView,
            wildcard: true,
        });
    };
    private createDefaultFilter = (project: ProjectResource): DeploymentOverviewFilters => {
        return {
            [DimensionTypes.Project]: {
                [project.Id]: true,
            },
            columnDimension: DimensionTypes.Environment,
            groupBy: project.TenantedDeploymentMode === TenantedDeploymentMode.Untenanted ? DimensionTypes.Channel : DimensionTypes.None,
            rowDimension: project.TenantedDeploymentMode === TenantedDeploymentMode.Untenanted ? DimensionTypes.Release : DimensionTypes.Tenant,
        };
    };
    static displayName = "DeploymentsOverviewInternal";
}
const deploymentsOverviewPagingSelectionKey = "octoDeploymentsOverviewPagingSelection";
export function DeploymentsOverview() {
    const projectContext = useProjectContext();
    const sampleProjectTourContext = useRequiredContext(SampleProjectTourContext);
    const dispatchAction = useProjectScopedAnalyticActionDispatch(projectContext.state.model.Id);
    const isPageHeaderVNextEnabled = useIsPageHeaderVNextEnabled();
    const [filters, setFilters] = useDeploymentsOverviewFilters(projectContext.state.model);
    const [pageState, setPageState] = usePageState(deploymentsOverviewPagingSelectionKey);
    return (<DeploymentsOverviewInternal filters={filters} setFilters={setFilters} projectContext={projectContext} sampleProjectTourContext={sampleProjectTourContext} dispatchAction={dispatchAction} isPageHeaderVNextEnabled={isPageHeaderVNextEnabled} pageState={pageState} setPageState={setPageState}/>);
}
